import { ButtonGroup, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogsBreederApi, DogsBreederGridDto, DogsBreederSearch } from "../../../../../../api";
import { CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../../components";
import { useAuth, useDialog } from "../../../../../../contexts";
import { ERoutes } from "../../../../../../ERoutes";
import { useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { DogsBreederPanelStack } from "./DogsBreederPanelStack";

export interface IDogBreederListProps {
  dogId: number;
  editMode?: boolean;
}

export const DogBreederList: React.FunctionComponent<IDogBreederListProps> = ({ dogId, editMode }) => {
  const dogsBreederApi = useApiService(DogsBreederApi);
  const { t } = useTl();
  const api = useApiService(DogsBreederApi);
  const { showDialogPromise } = useDialog();
  const { userPermissions } = useAuth();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { personName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogsBreederSearch) => {
      const s = DogsBreederSearch.fromJS({
        ...sObj,
        dogId: dogId,
      });
      setsobjState(s);
      return api.baseSearch(s);
    },
    [api, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const deleteCallback = React.useCallback(
    async (dogsBreederid: number) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult !== "yes") return;
      await dogsBreederApi.deleteDogsBreeder(dogsBreederid);
      search();
    },
    [dogsBreederApi, search, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      editMode && {
        fieldName: "_action",
        computed: true,
        disableSorting: true,
        autoFitContent: true,
        render: (row: DogsBreederGridDto) => <DeleteButton onDelete={() => deleteCallback(row.dogsBreederId)} />,
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "personName",
        render: (row: DogsBreederGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.personName}
          </Link>
        ),
      },
    ],
    [deleteCallback, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Breeders)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode &&
              userPermissions.some(
                (p) =>
                  p.module.toUpperCase() === "DOG" &&
                  p.permission.toUpperCase() === "E" &&
                  p.tab.toUpperCase() === "ADDBREEDER"
              ) && (
                <EditButton
                  onClick={(e) => {
                    e.stopPropagation();
                    addItemFunction();
                  }}
                  text={t(ETLCodes.General_Add)}
                  intent={Intent.PRIMARY}
                />
              )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.BreederSelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, height: 550, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <DogsBreederPanelStack
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
            dogId={dogId}
          />
        </div>
      </Dialog>
    </>
  );
};
